import { useState } from 'react';
import { format, parse, isValid } from 'date-fns';
import { ca, es } from 'date-fns/locale';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { CalendarIcon, X, Save, Cat, User, Syringe, Scissors } from 'lucide-react';
import { cn } from '../lib/utils';
import { updateAdoption } from '../lib/adoptionStorage';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const parseDMY = (s) => {
  if (!s) return undefined;
  const d = parse(s, 'dd/MM/yyyy', new Date());
  return isValid(d) ? d : undefined;
};
const formatDMY = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

const DateBox = ({ label, value, onChange, locale, testId }) => (
  <div>
    <Label className="text-xs font-medium text-stone-600 mb-1 flex items-center gap-1">
      <CalendarIcon size={12} /> {label}
    </Label>
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn('w-full justify-start text-left font-normal rounded-lg text-sm h-9', !value && 'text-stone-400')} data-testid={testId}>
          {value ? format(parseDMY(value), 'PPP', { locale }) : '—'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex items-center justify-between p-2 border-b">
          <span className="text-xs text-stone-500 px-2">{label}</span>
          {value && (
            <Button size="sm" variant="ghost" onClick={() => onChange('')} className="h-7 text-xs">
              <X size={12} className="mr-1" />Buidar
            </Button>
          )}
        </div>
        <Calendar mode="single" selected={parseDMY(value)} onSelect={(d) => d && onChange(formatDMY(d))} initialFocus />
      </PopoverContent>
    </Popover>
  </div>
);

export default function EditAdoptionModal({ record, onClose, onSaved }) {
  const { t, language } = useLanguage();
  const locale = language === 'es' ? es : ca;

  const [data, setData] = useState({
    nombre: record.nombre || '',
    apellidos: record.apellidos || '',
    telefono: record.telefono || '',
    adoptante_email: record.adoptante_email || '',
    nombre_gato: record.nombre_gato || '',
    microchip: record.microchip || '',
    data_naixement: record.data_naixement || '',
    esterilitzat: record.esterilitzat || 'no',
    fecha_adopcion: record.fecha_adopcion || '',
    fecha_vacunacion: record.fecha_vacunacion || '',
    fecha_vacunacion2: record.fecha_vacunacion2 || '',
    fecha_esterilizacion: record.fecha_esterilizacion || '',
  });

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  const handleSave = () => {
    if (data.microchip && !/^\d{15}$/.test(data.microchip)) {
      toast.error(t('form_microchip_error'));
      return;
    }
    // Actualitzem el registre bàsic i també el snapshot per als PDFs.
    const patch = {
      ...data,
      formDataSnapshot: { ...(record.formDataSnapshot || record), ...data },
    };
    updateAdoption(record.id, patch);
    toast.success(t('edit_saved'));
    onSaved?.();
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="edit-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-stone-100 sticky top-0 bg-white flex items-center justify-between rounded-t-3xl">
          <h2 className="font-bold text-stone-900 flex items-center gap-2">
            <Cat size={18} className="text-orange-600" /> {t('edit_title')}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="edit-close-btn">
            <X size={16} />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Adoptant */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2">
              <User size={12} /> {t('edit_section_adopter')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-stone-600 mb-1 block">{t('form_name')}</Label>
                <Input value={data.nombre} onChange={(e) => set('nombre', e.target.value)} className="h-9 text-sm rounded-lg" data-testid="edit-nombre" />
              </div>
              <div>
                <Label className="text-xs text-stone-600 mb-1 block">{t('form_surname')}</Label>
                <Input value={data.apellidos} onChange={(e) => set('apellidos', e.target.value)} className="h-9 text-sm rounded-lg" data-testid="edit-apellidos" />
              </div>
              <div>
                <Label className="text-xs text-stone-600 mb-1 block">{t('form_phone')}</Label>
                <Input value={data.telefono} onChange={(e) => set('telefono', e.target.value)} className="h-9 text-sm rounded-lg" data-testid="edit-telefono" />
              </div>
              <div>
                <Label className="text-xs text-stone-600 mb-1 block">Email</Label>
                <Input type="email" value={data.adoptante_email} onChange={(e) => set('adoptante_email', e.target.value)} className="h-9 text-sm rounded-lg" data-testid="edit-email" />
              </div>
            </div>
          </div>

          {/* Gat */}
          <div className="pt-4 border-t border-stone-100">
            <p className="text-xs font-semibold uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2">
              <Cat size={12} /> {t('edit_section_cat')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-stone-600 mb-1 block">{t('form_cat_name')}</Label>
                <Input value={data.nombre_gato} onChange={(e) => set('nombre_gato', e.target.value)} className="h-9 text-sm rounded-lg" data-testid="edit-nombre-gato" />
              </div>
              <div>
                <Label className="text-xs text-stone-600 mb-1 block">{t('form_microchip')}</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={15}
                  value={data.microchip}
                  onChange={(e) => set('microchip', e.target.value.replace(/\D/g, '').slice(0, 15))}
                  className={cn(
                    "h-9 text-sm rounded-lg",
                    data.microchip && !/^\d{15}$/.test(data.microchip) && "border-red-400"
                  )}
                  data-testid="edit-microchip"
                />
              </div>
              <DateBox label={t('form_cat_birth')} value={data.data_naixement} onChange={(v) => set('data_naixement', v)} locale={locale} testId="edit-date-birth" />
              <div>
                <Label className="text-xs text-stone-600 mb-1 block">{t('form_sterilized')}</Label>
                <RadioGroup value={data.esterilitzat} onValueChange={(v) => set('esterilitzat', v)} className="flex gap-4 h-9 items-center">
                  <div className="flex items-center gap-1"><RadioGroupItem value="si" id="edit-est-si" /><Label htmlFor="edit-est-si" className="text-sm">{t('form_yes')}</Label></div>
                  <div className="flex items-center gap-1"><RadioGroupItem value="no" id="edit-est-no" /><Label htmlFor="edit-est-no" className="text-sm">{t('form_no')}</Label></div>
                </RadioGroup>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="pt-4 border-t border-stone-100">
            <p className="text-xs font-semibold uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2">
              <CalendarIcon size={12} /> {t('edit_section_dates')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DateBox label={t('form_date_adoption')} value={data.fecha_adopcion} onChange={(v) => set('fecha_adopcion', v)} locale={locale} testId="edit-date-adopt" />
              <DateBox label={<><Scissors size={11} className="inline mr-1" />{t('form_date_sterilization')}</>} value={data.fecha_esterilizacion} onChange={(v) => set('fecha_esterilizacion', v)} locale={locale} testId="edit-date-ster" />
              <DateBox label={<><Syringe size={11} className="inline mr-1" />{t('form_date_vaccination')}</>} value={data.fecha_vacunacion} onChange={(v) => set('fecha_vacunacion', v)} locale={locale} testId="edit-date-vac" />
              <DateBox label={<><Syringe size={11} className="inline mr-1" />{t('form_date_vaccination2')}</>} value={data.fecha_vacunacion2} onChange={(v) => set('fecha_vacunacion2', v)} locale={locale} testId="edit-date-vac2" />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-stone-100 sticky bottom-0 bg-white flex justify-end gap-2 rounded-b-3xl">
          <Button variant="ghost" onClick={onClose} data-testid="edit-cancel-btn">{t('edit_cancel')}</Button>
          <Button onClick={handleSave} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="edit-save-btn">
            <Save size={14} className="mr-2" />{t('edit_save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
